/**
 * TrilingualInput — RU/KY/EN authoring control with a language switch and a
 * parity badge (P1 PIM-wave Task 1, `_shared` kit — CONTROLLER RULING D1:
 * shared, not feature-scoped, because Tasks 4/6 in other PIM surfaces consume
 * it). Every admin-authored trilingual value (field/group names, hints,
 * examples, option labels, category names) is entered through this control.
 *
 * Controlled component: all three language values always live in `value`, so
 * switching the active language never drops unsaved edits in the others (plan
 * line 4). No React Hook Form dependency here — callers wire it via RHF
 * `Controller` (brief §1).
 *
 * Language switch = `Segmented` (@garden/ui) — there is no Tabs primitive in
 * the kit (recon B2). The parity badge's three chips are a bespoke small
 * button (not `GPill`): `GPill`'s prop surface has no `title`/`aria-label`
 * passthrough (it destructures a closed prop set, no `...rest` spread), so it
 * cannot carry the per-chip accessible explanation the brief requires. The
 * chip mirrors GPill's `--g-*` tone tokens exactly (no new colors) and is
 * also click-to-switch, per brief §3.
 *
 * The `error` prop is a single, already-translated string rendered once below
 * the control with `role="alert"` (brief §1). `Field` is deliberately NOT
 * given its own `error`/`hint`/`label` — those would render Field's internal
 * (non-`role=alert`) helper line and duplicate both the label (this component
 * already renders one label for the whole tri-lingual control) and the error
 * text.
 */
import { useId, useState, type ChangeEvent, type ReactElement } from "react";
import { useTranslation } from "react-i18next";

import { Field, Segmented } from "@garden/ui";
import { ADMIN_NS, registerAdminI18n } from "@/features/admin/api";

export interface TrilingualValue {
  ru: string;
  ky: string;
  en: string;
}

type TrilingualLang = keyof TrilingualValue;

const LANGS: readonly TrilingualLang[] = ["ru", "ky", "en"];
const LANG_CODE: Record<TrilingualLang, string> = { ru: "RU", ky: "KG", en: "EN" };

function isTrilingualLang(value: string): value is TrilingualLang {
  return (LANGS as readonly string[]).includes(value);
}

/** Blank = empty-or-whitespace. Trim is for the presence check only — the
 * stored value itself is never mutated (brief §4). */
function isBlank(text: string): boolean {
  return text.trim() === "";
}

type ChipTone = "filled" | "neutral" | "warn" | "error";

/**
 * Resolves the parity-chip tone for one language slot. A non-blank slot is
 * always "filled" regardless of language. A blank RU slot is "error" only
 * when `required` (the RU-required marker, brief §1); otherwise "neutral".
 * A blank KY/EN slot is always "warn" — KY/EN are required-to-publish
 * independent of the `required` prop, which only governs the RU marker
 * (design-notes Surface 2 / plan line 4/23).
 */
function chipTone(lang: TrilingualLang, hasValue: boolean, required: boolean): ChipTone {
  if (hasValue) return "filled";
  if (lang === "ru") return required ? "error" : "neutral";
  return "warn";
}

const CHIP_TONE_CLASS: Record<ChipTone, string> = {
  filled: "border-transparent bg-primary text-primary-foreground",
  neutral: "border-border bg-card text-foreground",
  warn: "border-transparent bg-[var(--g-warn)]/10 text-[var(--g-warn)]",
  error: "border-transparent bg-[var(--g-error)]/10 text-[var(--g-error)]",
};

export interface TrilingualInputProps {
  /** Field label, already translated by the caller. */
  label: string;
  /** Controlled RU/KY/EN triple. */
  value: TrilingualValue;
  /** Change handler — receives the full next triple. */
  onChange: (next: TrilingualValue) => void;
  /** Renders a textarea instead of a single-line input. */
  multiline?: boolean;
  /** Shows the RU-required marker on the label; drives the RU chip's danger
   * tone when RU is blank. Does not block save (caller's Zod schema owns
   * actual validation). */
  required?: boolean;
  disabled?: boolean;
  /** Already-translated error text, rendered once below the control with
   * `role="alert"`. */
  error?: string | null;
  /** Tighter spacing for inline-in-row use (OptionsEditor, Task 4). */
  compact?: boolean;
  /** Base id for a11y wiring; per-language input ids derive from it. */
  id?: string;
}

/**
 * Renders the trilingual authoring control. `data-testid="trilingual-input"`
 * on the wrapper; the active per-language control is
 * `trilingual-input-field` (single-line) or `trilingual-input-textarea`
 * (multiline); each parity chip is `trilingual-input-chip` with
 * `data-lang`/`data-tone`.
 */
export function TrilingualInput({
  label,
  value,
  onChange,
  multiline = false,
  required = false,
  disabled = false,
  error = null,
  compact = false,
  id,
}: TrilingualInputProps): ReactElement {
  registerAdminI18n();
  const { t } = useTranslation(ADMIN_NS);
  const reactId = useId();
  const baseId = id ?? `trilingual-${reactId.replace(/:/g, "")}`;
  const [activeLang, setActiveLang] = useState<TrilingualLang>("ru");

  const switchLang = (lang: TrilingualLang): void => {
    if (disabled) return;
    setActiveLang(lang);
  };

  const setLangValue = (next: string): void => {
    onChange({ ...value, [activeLang]: next });
  };

  const activeInputId = `${baseId}-${activeLang}`;
  const errorId = `${baseId}-error`;
  const hasError = Boolean(error);
  const activeAriaLabel = `${label} — ${LANG_CODE[activeLang]}`;

  const gapClass = compact ? "gap-1.5" : "gap-2";
  const labelClass = compact
    ? "text-xs font-semibold text-foreground"
    : "text-[13px] font-semibold text-foreground";

  return (
    <div className={["flex flex-col", gapClass].join(" ")} data-testid="trilingual-input">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <label htmlFor={activeInputId} className={labelClass}>
          {label}
          {required && (
            <>
              <span aria-hidden="true" className="ml-0.5 text-[var(--g-error)]">
                *
              </span>
              <span className="sr-only">{t("pim.trilingual.requiredSr")}</span>
            </>
          )}
        </label>
        <div
          className="flex items-center gap-1"
          role="group"
          aria-label={t("pim.trilingual.parityGroupLabel")}
        >
          {LANGS.map((lang) => {
            const hasValue = !isBlank(value[lang]);
            const tone = chipTone(lang, hasValue, required);
            const code = LANG_CODE[lang];
            let explanation: string | undefined;
            if (tone === "error") {
              explanation = t("pim.trilingual.missingRequiredHint", { code });
            } else if (tone === "warn") {
              explanation = t("pim.trilingual.missingTranslationHint", { code });
            }
            const switchLabel = t("pim.trilingual.switchTo", { code });
            const ariaLabel = explanation ? `${switchLabel} — ${explanation}` : switchLabel;
            const isActive = activeLang === lang;
            return (
              <button
                key={lang}
                type="button"
                data-testid="trilingual-input-chip"
                data-lang={lang}
                data-tone={tone}
                data-active={isActive ? "true" : "false"}
                disabled={disabled}
                title={explanation}
                aria-label={ariaLabel}
                onClick={() => switchLang(lang)}
                className={[
                  "inline-flex h-6 min-w-6 items-center justify-center rounded-full border px-2 text-[11px] font-semibold uppercase tracking-wide transition-colors",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-[var(--g-surface)]",
                  "disabled:cursor-not-allowed disabled:opacity-60",
                  CHIP_TONE_CLASS[tone],
                  isActive ? "ring-2 ring-[var(--g-primary)] ring-offset-1 ring-offset-[var(--g-surface)]" : "",
                ].join(" ")}
              >
                {code}
              </button>
            );
          })}
        </div>
      </div>

      <div
        className={disabled ? "pointer-events-none opacity-60" : undefined}
        aria-disabled={disabled || undefined}
      >
        <Segmented
          options={LANGS.map((lang) => ({ value: lang, label: LANG_CODE[lang] }))}
          value={activeLang}
          onChange={(next) => {
            if (isTrilingualLang(next)) switchLang(next);
          }}
        />
      </div>

      {multiline ? (
        <textarea
          id={activeInputId}
          data-testid="trilingual-input-textarea"
          rows={compact ? 2 : 3}
          disabled={disabled}
          aria-label={activeAriaLabel}
          aria-describedby={hasError ? errorId : undefined}
          aria-invalid={hasError || undefined}
          value={value[activeLang]}
          onChange={(event: ChangeEvent<HTMLTextAreaElement>) => setLangValue(event.target.value)}
          className={[
            "w-full resize-none rounded-[var(--g-radius-sm)] border bg-[var(--g-surface)] px-3 text-sm text-foreground",
            "outline-none focus:ring-2 focus:ring-[var(--g-primary)]",
            "disabled:cursor-not-allowed disabled:opacity-60",
            compact ? "py-1.5" : "py-2",
            hasError ? "border-[var(--g-error)]" : "border-[var(--g-hairline)]",
          ].join(" ")}
        />
      ) : (
        <Field
          id={activeInputId}
          data-testid="trilingual-input-field"
          disabled={disabled}
          aria-label={activeAriaLabel}
          aria-describedby={hasError ? errorId : undefined}
          aria-invalid={hasError || undefined}
          value={value[activeLang]}
          onChange={(event: ChangeEvent<HTMLInputElement>) => setLangValue(event.target.value)}
        />
      )}

      {hasError && (
        <p id={errorId} role="alert" data-testid="trilingual-input-error" className="text-xs text-[var(--g-error)]">
          {error}
        </p>
      )}
    </div>
  );
}

export default TrilingualInput;
